import React, { useState, useEffect } from 'react';
import { Button, Div, Text } from '@pinpoint/design-kit';

import pinpoint from 'src/themes/pinpoint';
import pinpoint1 from 'src/themes/pinpoint1';
import pinpoint2 from 'src/themes/pinpoint2';
import pinpoint3 from 'src/themes/pinpoint3';
import pinpoint4 from 'src/themes/pinpoint4';
import pinpoint5 from 'src/themes/pinpoint5';
import pinpoint6 from 'src/themes/pinpoint6';
import hit from 'src/themes/tags/hit';
import miss from 'src/themes/tags/miss';
import validation from 'src/themes/tags/validation';
import target from 'src/themes/tags/target';
import actuarial from 'src/themes/tags/actuarial';

export { default as ErrorBoundary } from './error_boundary';

export { default as insights } from './insights';

/* eslint-disable react/prop-types */

const map = {
  pinpoint,
  pinpoint1,
  pinpoint2,
  pinpoint3,
  pinpoint4,
  pinpoint5,
  pinpoint6,
  hit,
  cluster: hit,
  miss,
  validation,
  target,
  actuarial,
  'hi freq': actuarial,
  'lo freq': actuarial,
};

export function GradientSelection({ hidden, style, setGradient, onGradientChange }) {
  if (hidden) return null;
  return (
    <Div d="flex" align="center" justify="flex-end" h="2.5rem">
      <Div
        tag="span"
        h="2.5rem"
        w="2.5rem"
        rounded="md"
        border="1px solid"
        borderColor="shadeE6"
        cursor="pointer"
        hoverBorderColor="shadeCC"
        data-tip="Blue-orange gradient"
        style={{
          ...style.COLORS.BLUE_ORANGE_CSS,
        }}
        onClick={() => {
          setGradient(style.COLORS.GRADIENTS.BLUE_ORANGE);
          onGradientChange(style.COLORS.GRADIENTS.BLUE_ORANGE);
        }}
        m={{ l: '0.4rem' }}
      />
      <Div
        tag="span"
        h="2.5rem"
        w="2.5rem"
        rounded="md"
        border="1px solid"
        borderColor="shadeE6"
        cursor="pointer"
        hoverBorderColor="shadeCC"
        data-tip="Blue-red gradient"
        style={{
          ...style.COLORS.ELECTRIC_BLUE_RED_CSS,
        }}
        onClick={() => {
          setGradient(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
          onGradientChange(style.COLORS.GRADIENTS.ELECTRIC_BLUE_RED);
        }}
        m={{ l: '0.4rem' }}
      />
      <Div
        tag="span"
        h="2.5rem"
        w="2.5rem"
        rounded="md"
        border="1px solid"
        borderColor="shadeE6"
        cursor="pointer"
        hoverBorderColor="shadeCC"
        data-tip="Full-spectrum gradient"
        style={{
          ...style.COLORS.MULTICHROMATIC_CSS,
        }}
        onClick={() => {
          setGradient(style.COLORS.GRADIENTS.MULTICHROMA);
          onGradientChange(style.COLORS.GRADIENTS.MULTICHROMA);
        }}
        m={{ l: '0.4rem' }}
      />
    </Div>
  );
}

export function Palette({ selected, onSelect, style, hidden, defaultTheme = 'pinpoint' }) {
  if (hidden) return null;
  return (
    <Div d="flex" align="center" justify="flex-start" h="2.5rem" style={style}>
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={
          ['pinpoint', 'hit', 'miss', 'validation', 'target', 'actuarial'].includes(selected)
            ? 'tabSelected'
            : 'tabRest'
        }
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: map[defaultTheme].color[0],
        }}
        cursor="pointer"
        onClick={() => onSelect(defaultTheme)}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint1' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint1.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint1');
        }}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint2' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint2.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint2');
        }}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint3' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint3.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint3');
        }}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint4' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint4.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint4');
        }}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint5' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint5.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint5');
        }}
      />
      <Div
        w="1rem"
        h="1rem"
        rounded="circle"
        border="2px solid"
        borderColor={selected === 'pinpoint6' ? 'tabSelected' : 'tabRest'}
        hoverBorderColor="tabSelected"
        m={{ l: '0.4rem' }}
        style={{
          backgroundColor: pinpoint6.color[0],
        }}
        cursor="pointer"
        onClick={() => {
          onSelect('pinpoint6');
        }}
      />
    </Div>
  );
}

export function AnimatedButton({ animationId, bg = 'brand500', hoverBg = 'brand300', onClick = () => null, ...rest }) {
  const [_bg, setBg] = useState(bg);
  useEffect(() => {
    let counter = 0;
    const isAnimationShown = localStorage.getItem(animationId);
    if (!isAnimationShown) {
      const interval = setInterval(() => {
        if (counter) setBg(bg);
        else setBg(hoverBg);
        counter = (counter + 1) % 2;
        const isAnimationShown1 = localStorage.getItem(animationId);
        if (isAnimationShown1) {
          setBg(bg);
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
    return () => null;
  }, []);
  return (
    <Button
      bg={_bg}
      transition={false}
      style={{
        transition: 'all 1s ease-in-out',
      }}
      onClick={(e) => {
        localStorage.setItem(animationId, 1);
        onClick(e);
      }}
      {...rest}
    />
  );
}

export const withFixedTitle = (Wrapped) =>
  function ({ options = {}, ...props }) {
    const newProps = {
      options: {
        title: {
          show: false,
        },
        ...options,
      },
    };
    const { w = '100%', h = '100%', title = 'Chart', containerStyle = {}, styleOverride = {}, titleStyle = {} } = props;
    return (
      <Div
        w={w}
        h={h}
        d="flex"
        flexDir="column"
        justify="start"
        align="center"
        {...containerStyle}
        style={{
          ...styleOverride,
        }}
      >
        <Text
          fontFamily="secondary"
          textSize="title"
          textWeight="400"
          textColor="tabSelected"
          {...titleStyle}
          style={{
            alignSelf: 'start',
          }}
          m={{ b: '1rem' }}
        >
          {title}
        </Text>
        <Div w="100%" minH="0" flexGrow="1">
          <Wrapped {...props} {...newProps} />
        </Div>
      </Div>
    );
  };

export class EventEmitter {
  constructor() {
    this.events = {};
  }

  dispatch(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach((cb) => cb(data));
  }

  subscribe(event, cb) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(cb);
  }
}

EventEmitter.events = {};

/* eslint-enable react/prop-types */
